import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, RootDispatch } from "../../store";
import {
  fetchProductionCosts,
  ProductionCostItem,
} from "../../store/slices/productionCostSlice";

const formatDate = (dateString: string): string => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString();
};

const formatCurrency = (amount: number | undefined): string => {
  const validAmount = typeof amount === "number" && !isNaN(amount) ? amount : 0;
  return `₹${validAmount.toFixed(2)}`;
};

const ProductionCostPage: React.FC = () => {
  const dispatch: RootDispatch = useDispatch();

  const { items, pagination, loading, error } = useSelector(
    (state: RootState) => state.productionCost,
  );

  const [page, setPage] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const limit: number = 10;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(1);
    }, 800);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    dispatch(fetchProductionCosts({ page, limit, search: debouncedSearch }));
  }, [dispatch, page, limit, debouncedSearch]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const toggleRow = useCallback((id: string) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) newSet.delete(id);
      else newSet.add(id);
      return newSet;
    });
  }, []);

  const thClass =
    "px-4 py-3 text-xs font-semibold uppercase tracking-wider text-center text-gray-600 bg-gray-100";
  const tdClass =
    "px-4 py-4 whitespace-nowrap text-sm text-gray-800 text-center";

  if (error) {
    return (
      <div className="p-8 text-center bg-red-100 border-l-4 border-red-500 m-4 rounded-lg w-full">
        <p className="text-red-700 font-bold">{error}</p>
        <button
          onClick={() =>
            dispatch(
              fetchProductionCosts({ page, limit, search: debouncedSearch }),
            )
          }
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 w-full">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-6">
        <div className="bg-blue-50/50 px-6 py-3 border-b border-gray-100 rounded-t-2xl">
          <h1 className="text-xl font-bold text-gray-800 flex items-center">
            Production Costs Overview
          </h1>
        </div>
        <div className="p-4 sm:p-6 flex justify-between items-center">
          <input
            type="text"
            placeholder="Search by Serial, Logger..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full max-w-sm p-2 border border-slate-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-all text-sm shadow-sm"
          />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-blue-600">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-600 mx-auto mb-2"></div>
            Loading production cost data...
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-gray-500">No data found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className={thClass}>Product</th>
                  <th className={thClass}>Serial No.</th>
                  <th className={thClass}>Material Cost</th>
                  <th className={thClass}>Travel Cost</th>
                  <th className={thClass}>Other Cost</th>
                  <th className={thClass}>Transfer Cost</th>
                  <th className={thClass}>Total Cost</th>
                  <th className={`${thClass} w-16`}></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {items.map((item: ProductionCostItem) => (
                  <React.Fragment key={item._id}>
                    <tr className="hover:bg-gray-50 transition-colors duration-150">
                      <td className={tdClass}>{item.productName}</td>
                      <td className={`${tdClass} pr-6`}>
                        <div className="flex items-center justify-center gap-3">
                          <strong className="font-semibold text-blue-600">
                            {item.serialNumber}
                          </strong>
                          <button
                            onClick={() => toggleRow(item._id!)}
                            className="text-xs text-gray-500 hover:text-blue-600 underline"
                            title="Show more IDs"
                          >
                            {expandedRows.has(item._id!) ? "Hide" : "More"}
                          </button>
                        </div>

                        {expandedRows.has(item._id!) && (
                          <div className="mt-1 text-xs text-gray-600 space-y-0.5">
                            {item.loggerSerialNumber && (
                              <div className="flex items-center justify-center gap-3">
                                <span className="font-medium">Logger:</span>
                                <span className="text-gray-700">
                                  {item.loggerSerialNumber}
                                </span>
                              </div>
                            )}
                            {item.manufacturingSerialNumber && (
                              <div className="flex items-center justify-center gap-3">
                                <span className="font-medium">Mfg:</span>
                                <span className="text-gray-700">
                                  {item.manufacturingSerialNumber}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className={tdClass}>
                        {formatCurrency(item.totalMaterialCost)}
                      </td>
                      <td className={tdClass}>
                        {formatCurrency(item.totalTravelCost)}
                      </td>
                      <td className={tdClass}>
                        {formatCurrency(item.totalOtherCost)}
                      </td>
                      <td className={tdClass}>
                        {formatCurrency(item.totalTransferCost)}
                      </td>
                      <td className={`${tdClass} font-bold text-green-700`}>
                        {formatCurrency(item.totalCost)}
                      </td>
                      <td className={`${tdClass} p-2 text-center`}>
                        <button
                          onClick={() => toggleRow(item._id!)}
                          className="inline-flex items-center justify-center w-8 h-8 p-1 rounded-full text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 shadow-sm"
                          title={
                            expandedRows.has(item._id!)
                              ? "Hide breakdown"
                              : "Show breakdown"
                          }
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d={
                                expandedRows.has(item._id!)
                                  ? "M5 10l7 7 7-7"
                                  : "M19 14l-7 7-7-7"
                              }
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>
                    {expandedRows.has(item._id!) && (
                      <tr>
                        <td colSpan={8} className="p-0 bg-gray-50">
                          <div className="p-6 border-t border-gray-200">
                            <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-lg bg-white/80 backdrop-blur-sm">
                              <div className="p-5 bg-gradient-to-r from-blue-50/70 to-indigo-50/50 rounded-t-2xl border-b border-gray-200">
                                <h5 className="text-sm font-bold text-gray-800 uppercase tracking-wider text-center">
                                  Raw Material Cost Breakdown
                                </h5>
                              </div>
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-center text-gray-600">
                                      Raw Material
                                    </th>
                                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-center text-gray-600">
                                      Qty
                                    </th>
                                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-center text-gray-600">
                                      Material Cost
                                    </th>
                                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-center text-gray-600">
                                      Travel Cost
                                    </th>
                                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-center text-gray-600">
                                      Other Cost
                                    </th>
                                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-center text-gray-600">
                                      Transfer Cost
                                    </th>
                                    <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-center text-gray-600">
                                      Total
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 bg-white">
                                  {(item.materialsUsed || []).map(
                                    (mat: any, idx: number) => (
                                      <tr
                                        key={idx}
                                        className="hover:bg-gradient-to-r hover:from-blue-50/30 hover:to-indigo-50/20 transition-all duration-200 group"
                                      >
                                        <td className="px-5 py-4 text-sm font-semibold text-indigo-900 text-center bg-gradient-to-r from-blue-50/40 to-transparent group-hover:from-blue-100/60 transition-colors">
                                          {mat.rawMaterialName || "N/A"}
                                        </td>
                                        <td className="px-5 py-4 text-sm text-gray-700 text-center font-medium">
                                          {mat.quantityUsed
                                            ? mat.quantityUsed.toFixed(2)
                                            : "0"}{" "}
                                          <span className="text-sm text-gray-500">
                                            {mat.convertedUnit || ""}
                                          </span>
                                        </td>
                                        <td className="px-5 py-4 text-sm text-emerald-700 font-semibold text-center">
                                          {formatCurrency(mat.materialCost)}
                                        </td>
                                        <td className="px-5 py-4 text-sm text-orange-600 font-medium text-center">
                                          {formatCurrency(mat.travelCost)}
                                        </td>
                                        <td className="px-5 py-4 text-sm text-purple-600 font-medium text-center">
                                          {formatCurrency(mat.otherCost)}
                                        </td>
                                        <td className="px-5 py-4 text-sm text-blue-600 font-medium text-center">
                                          {formatCurrency(mat.transferCost)}
                                        </td>
                                        <td className="px-5 py-4 text-sm font-bold text-indigo-800 text-center bg-gradient-to-l from-blue-100/50 to-transparent">
                                          {formatCurrency(mat.totalCost)}
                                        </td>
                                      </tr>
                                    ),
                                  )}
                                </tbody>
                                <tfoot>
                                  <tr className="bg-gradient-to-r from-gray-50 to-blue-50/30 border-t-2 border-gray-200">
                                    <th
                                      className="px-4 py-4 text-sm font-bold text-gray-900 text-center"
                                      colSpan={2}
                                    >
                                      Grand Totals
                                    </th>
                                    <td className="px-4 py-4 text-sm font-bold text-emerald-700 text-center">
                                      {formatCurrency(item.totalMaterialCost)}
                                    </td>
                                    <td className="px-4 py-4 text-sm font-bold text-orange-600 text-center">
                                      {formatCurrency(item.totalTravelCost)}
                                    </td>
                                    <td className="px-4 py-4 text-sm font-bold text-purple-600 text-center">
                                      {formatCurrency(item.totalOtherCost)}
                                    </td>
                                    <td className="px-4 py-4 text-sm font-bold text-blue-600 text-center">
                                      {formatCurrency(item.totalTransferCost)}
                                    </td>
                                    <td className="px-4 py-4 text-lg font-black text-emerald-800 text-center bg-emerald-50/60 border-l-4 border-emerald-200">
                                      {formatCurrency(item.totalCost)}
                                    </td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination.totalPages > 1 && !loading && (
          <div className="p-3 flex justify-between items-center border-t border-gray-200 bg-gray-50 rounded-b-2xl">
            <p className="text-sm text-gray-600">
              Showing <strong>{page}</strong> of{" "}
              <strong>{pagination.totalPages}</strong> pages (
              <strong>{pagination.totalItems ?? 0}</strong> total items)
            </p>

            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className={`flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                  page === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-300"
                    : "bg-white text-blue-600 border-blue-600/50 hover:bg-blue-50"
                }`}
              >
                <svg
                  className="w-3 h-3 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Previous
              </button>

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === pagination.totalPages}
                className={`flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                  page === pagination.totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-300"
                    : "bg-white text-blue-600 border-blue-600/50 hover:bg-blue-50"
                }`}
              >
                Next
                <svg
                  className="w-3 h-3 ml-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7-7"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductionCostPage;
